import { Stack } from '@fluentui/react';
import { PageHeader, useAppNavigationContext } from '@h2oai/ui-kit';
import React from 'react';

import { useQueryPermissionCurrentUser } from '../../authz/authz.hooks';
import type { Tag } from '../../authz/gen/ai/h2o/workspace/v1/tag_pb';
import type { Workspace } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { WorkspacesUserActionType } from '../../authz/userActions';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import WidgetList, { type WidgetListProps } from '../../components/WidgetList/WidgetList';
import { stackStylesNoLeftNav } from '../../themes/themes';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { AddEditWorkspacePanel } from './components/AddEditWorkspacePanel/AddEditWorkspacePanel';
import { WorkspaceListRow } from './components/WorkspaceListRow';
import { WorkspaceOrderBy } from './components/WorkspaceOrderBy/WorkspaceOrderBy';
import { useColumns } from './hooks/useColumns';
import { useCombinedWorkspaces } from './hooks/useCombinedWorkspaces';
import { useOrderBy } from './hooks/useOrderBy';
import type { WorkspacesListProps } from './types';

export const WorkspacesList = (props: WorkspacesListProps) => {
  const {
    workspacesQuery,
    defaultWorkspaceQuery,
    workspacesByOwner,
    topPanelSlot,
    searchText = '',
    onSearchChange,
    tagsQuery,
    filters,
    orderBy: orderByProp,
    setOrderBy: setOrderByProp,
  } = props;
  const { disableWorkspacesDropdown, workspacesDisabled } = useAppNavigationContext();
  const { orderBy: fallbackOrderBy, setOrderBy: fallbackSetOrderBy } = useOrderBy();

  const orderBy = orderByProp ?? fallbackOrderBy;
  const setOrderBy = setOrderByProp ?? fallbackSetOrderBy;

  const canCreateQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_CREATE,
    resource: '//workspaceserver',
  });
  const canCreate = Boolean(canCreateQuery.data?.allowed);

  const tags = tagsQuery?.data?.pages as Tag[] | undefined;
  const tagsMap = React.useMemo(() => {
    const map = new Map<string, Tag>();
    tags?.forEach((tag) => {
      if (tag.name) {
        map.set(tag.name, tag);
      }
    });
    return map;
  }, [tags]);

  const { allWorkspaces, isLoadingMore, onLoadMore, isLoadingSearch } = useCombinedWorkspaces({
    defaultWorkspaceQuery,
    workspacesQuery,
    workspacesByOwner,
    searchText,
  });

  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const workspacesWithActiveFlag = React.useMemo(
    () =>
      allWorkspaces?.map((workspace) => ({
        ...workspace,
        isActive: workspace.name === ACTIVE_WORKSPACE_NAME,
      })),
    [allWorkspaces, ACTIVE_WORKSPACE_NAME]
  );

  const [modalWindow, setModalWindow] = React.useState<
    undefined | { tag: `EDIT`; workspace?: Workspace } | { tag: `VIEW`; workspace?: Workspace } | { tag: `NEW` }
  >();
  const handleCloseModalWindow = React.useCallback(() => setModalWindow(undefined), []);
  const columns = useColumns({
    setWorkspaceForEdit: (workspace) => setModalWindow({ tag: `EDIT`, workspace }),
    setWorkspaceForView: (workspace) => setModalWindow({ tag: `VIEW`, workspace }),
  });

  const hasActiveFilters = React.useMemo(() => Boolean(filters && Object.keys(filters).length > 0), [filters]);

  const widgetListProps: WidgetListProps = {
    columns,
    items: workspacesWithActiveFlag,
    loading: workspacesQuery.isFetching && !workspacesQuery.data,
    delayLoader: false,
    isLoadingMore,
    onLoadMore,
    isLoadingSearch,
    searchProps: {
      onSearchChange: onSearchChange || (() => {}),
      placeholder: 'Search workspaces',
      searchText,
    },
    forceShowActionBar: Boolean(searchText) || hasActiveFilters,
    onRenderRow: (props) => {
      if (!props) return null;
      return <WorkspaceListRow item={props.item} columns={props.columns} tagsMap={tagsMap} />;
    },
    customActions:
      orderBy && setOrderBy ? (
        <WorkspaceOrderBy
          descending={orderBy.descending}
          orderKey={orderBy.key}
          onChange={(key, descending) => setOrderBy({ key, descending })}
        />
      ) : undefined,
    topPanelSlot,
    actionProps: canCreate
      ? {
          actionIcon: 'Add',
          actionTitle: 'Create workspace',
          onActionClick: () => setModalWindow({ tag: `NEW` }),
        }
      : undefined,
    NoItemsContent: NoItemView(
      hasActiveFilters || searchText
        ? {
            title: 'No workspaces matching filters',
            description: 'Try adjusting your filter criteria or clear the filters to see all workspaces.',
          }
        : {
            title: 'Workspaces',
            description: 'There are no workspaces available to select from. Create the first one to begin.',
            actionTitle: 'Create workspace',
            onActionClick: () => setModalWindow({ tag: `NEW` }),
            actionIcon: 'Add',
          }
    ),
    ErrorContent: FailedToLoadView({
      title: 'Failed to load workspaces',
      description: 'Please try again later. If the problem persists, contact our support.',
      actionTitle: 'Retry',
      onActionClick: () => workspacesQuery.refetch(),
      actionIcon: 'Refresh',
    }),
    compact: true,
  };

  React.useEffect(() => {
    if (!workspacesDisabled) disableWorkspacesDropdown();
  }, [workspacesDisabled, disableWorkspacesDropdown]);

  return (
    <Stack styles={stackStylesNoLeftNav}>
      <PageHeader pageTitle="Workspaces" />
      <WidgetList {...widgetListProps} styles={{ widgetContainer: { paddingTop: 20 } }} />

      <AddEditWorkspacePanel
        isOpen={Boolean(modalWindow)}
        onDismiss={handleCloseModalWindow}
        onWorkspaceSave={handleCloseModalWindow}
        workspaceForEdit={
          modalWindow?.tag === 'EDIT' || modalWindow?.tag === 'VIEW' ? modalWindow.workspace : undefined
        }
        availableTags={tags}
        readOnly={modalWindow?.tag === 'VIEW'}
      />
    </Stack>
  );
};
